import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  GraduationCap,
  Users,
  Star,
  Briefcase,
  LogIn,
  Mail,
  Database,
  Trash2,
  Download,
  Plus,
  ExternalLink,
  Clock,
  Save,
  Server,
  RefreshCw,
  Image,
  Info,
  BarChart3,
  HardDrive,
  Receipt,
  Construction,
} from 'lucide-react';
import { V2DeploymentStatus } from '@/components/schools/V2DeploymentStatus';
import { V2DeployDialog } from '@/components/schools/V2DeployDialog';
import {
  GeneralInfoCard,
  ContactsCard,
  V1DeploymentCard,
  V2SubscriptionCard,
  BrandingImagesCard,
  WhiteLabelCard,
  V1DatabaseCard,
  V2DirectorsCard,
} from '@/components/schools/detail';
import {
  getSchoolForEdit,
  getSchoolOptions,
  getSchoolMetrics,
  getSchoolMetricsHistory,
  getSchoolBackups,
  createSchoolBackup,
  deleteSchoolBackup,
  getBackupDownloadUrl,
} from '@/api/schools';
import { getV2Progress, resetV2Deployment, migrateV2, changeV2Status } from '@/api/v2-deployment';
import { getV2TenantGroups } from '@/api/white-labels';
import type { SchoolEditData, SchoolOptions } from '@/types/school';
import type { SchoolMetrics, MetricsHistory, Backup } from '@/api/schools';
import type { V2DeploymentInfo, V2TenantGroup } from '@/types/v2-deployment';
import { cn } from '@/lib/utils';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'teal' | 'orange' | 'yellow' | 'blue' | 'red' | 'cyan' | 'pink';
}

function StatCard({ title, value, icon: Icon, color }: StatCardProps) {
  const iconBgClasses = {
    teal: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    yellow: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    cyan: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
    pink: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm">
      <div className="p-4 flex items-center gap-3">
        <div className={cn('p-2.5 rounded-lg', iconBgClasses[color])}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        </div>
      </div>
    </div>
  );
}

function getStatusBadgeVariant(
  status: string | null
): 'success' | 'warning' | 'default' | 'destructive' {
  switch (status) {
    case 'production':
      return 'success';
    case 'demo_empty':
    case 'demo_datas':
      return 'warning';
    case 'cancelled':
      return 'destructive';
    default:
      return 'default';
  }
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function SchoolDetailPage() {
  const { code } = useParams<{ code: string }>();
  const { isFavorite, toggleFavorite } = useAuth();
  const [school, setSchool] = useState<SchoolEditData | null>(null);
  const [options, setOptions] = useState<SchoolOptions | null>(null);
  const [metrics, setMetrics] = useState<SchoolMetrics | null>(null);
  const [metricsHistory, setMetricsHistory] = useState<MetricsHistory[]>([]);
  const [metricsHistoryLoading, setMetricsHistoryLoading] = useState(false);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('informations');
  const [v2DeployDialogOpen, setV2DeployDialogOpen] = useState(false);
  const [v2Info, setV2Info] = useState<V2DeploymentInfo | null>(null);
  const [v2Loading, setV2Loading] = useState(false);
  const [v2Resetting, setV2Resetting] = useState(false);
  const [v2Suspending, setV2Suspending] = useState(false);
  const [v2MigrateDialogOpen, setV2MigrateDialogOpen] = useState(false);
  const [v2MigrateLoading, setV2MigrateLoading] = useState(false);
  const [v2TenantGroups, setV2TenantGroups] = useState<V2TenantGroup[]>([]);
  const [v2SelectedGroupId, setV2SelectedGroupId] = useState<string>('');

  useEffect(() => {
    if (code) {
      fetchData();
      fetchV2Info();
    }
  }, [code]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [schoolData, optionsData, metricsData] = await Promise.all([
        getSchoolForEdit(code!),
        getSchoolOptions(),
        getSchoolMetrics(code!),
      ]);
      setSchool(schoolData);
      setOptions(optionsData);
      setMetrics(metricsData);
    } catch (err) {
      setError("Erreur lors du chargement de l'école");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshSchool = useCallback(async () => {
    if (!code) return;
    try {
      const [schoolData, metricsData] = await Promise.all([
        getSchoolForEdit(code),
        getSchoolMetrics(code),
      ]);
      setSchool(schoolData);
      setMetrics(metricsData);
    } catch (err) {
      console.error('Error refreshing school:', err);
    }
  }, [code]);

  const refreshOptions = useCallback(async () => {
    try {
      const data = await getSchoolOptions();
      setOptions(data);
    } catch (err) {
      console.error('Failed to refresh options:', err);
    }
  }, []);

  const fetchBackups = async () => {
    try {
      setBackupsLoading(true);
      const data = await getSchoolBackups(code!);
      setBackups(data);
    } catch (err) {
      console.error('Error fetching backups:', err);
    } finally {
      setBackupsLoading(false);
    }
  };

  // Load backups when switching to the tab
  useEffect(() => {
    if (activeTab === 'backups' && backups.length === 0 && !backupsLoading) {
      fetchBackups();
    }
  }, [activeTab]);

  // Refresh V2 info when switching to V2 tab
  useEffect(() => {
    if (activeTab === 'v2' && code && !v2Loading) {
      fetchV2Info();
    }
  }, [activeTab]);

  // Load metrics history when switching to statistiques tab
  useEffect(() => {
    if (activeTab === 'statistiques' && code && metricsHistory.length === 0 && !metricsHistoryLoading) {
      fetchMetricsHistory();
    }
  }, [activeTab]);

  const fetchMetricsHistory = async () => {
    if (!code) return;
    try {
      setMetricsHistoryLoading(true);
      const history = await getSchoolMetricsHistory(code);
      setMetricsHistory(history);
    } catch (err) {
      console.error('Erreur lors du chargement de l\'historique des métriques', err);
    } finally {
      setMetricsHistoryLoading(false);
    }
  };

  const fetchV2Info = async () => {
    if (!code) return;
    try {
      setV2Loading(true);
      const info = await getV2Progress(code);
      setV2Info(info);
    } catch {
      setV2Info(null);
    } finally {
      setV2Loading(false);
    }
  };

  const handleV2Reset = async () => {
    if (!code) return;
    if (
      !confirm(
        'Êtes-vous sûr de vouloir réinitialiser le déploiement V2 ? Cela supprimera le tenant V2 et effacera toutes les données associées.'
      )
    ) {
      return;
    }

    try {
      setV2Resetting(true);
      await resetV2Deployment(code);
      setV2Info(null);
      await refreshSchool();
    } catch (err) {
      console.error('Error resetting V2:', err);
      alert('Erreur lors de la réinitialisation: ' + (err as Error).message);
    } finally {
      setV2Resetting(false);
    }
  };

  const handleV2Suspend = async () => {
    if (!code || !v2Info) return;

    const currentStatus = v2Info.tenant?.status || 'ACTIVE';
    const newStatus = currentStatus === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
    const action = newStatus === 'SUSPENDED' ? 'suspendre' : 'réactiver';

    if (!confirm(`Êtes-vous sûr de vouloir ${action} cette école en V2 ?`)) {
      return;
    }

    try {
      setV2Suspending(true);
      await changeV2Status(code, { status: newStatus });
      await fetchV2Info();
    } catch (err) {
      console.error('Error changing V2 status:', err);
      alert(`Erreur lors de l'action: ` + (err as Error).message);
    } finally {
      setV2Suspending(false);
    }
  };

  const openMigrateDialog = async () => {
    try {
      const result = await getV2TenantGroups();
      setV2TenantGroups(result.data.filter((g) => g.id !== v2Info?.tenantGroupId));
      setV2SelectedGroupId('');
      setV2MigrateDialogOpen(true);
    } catch (err) {
      console.error('Error loading tenant groups:', err);
      alert('Erreur lors du chargement des réseaux: ' + (err as Error).message);
    }
  };

  const handleV2Migrate = async () => {
    if (!code || !v2SelectedGroupId) return;

    try {
      setV2MigrateLoading(true);
      await migrateV2(code, { targetTenantGroupId: v2SelectedGroupId });
      setV2MigrateDialogOpen(false);
      await fetchV2Info();
      await refreshSchool();
    } catch (err) {
      console.error('Error migrating V2:', err);
      alert('Erreur lors de la migration: ' + (err as Error).message);
    } finally {
      setV2MigrateLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      await createSchoolBackup(code!);
      await fetchBackups();
    } catch (err) {
      console.error('Error creating backup:', err);
    }
  };

  const handleDeleteBackup = async (backupId: number) => {
    if (!confirm('Etes-vous sur de vouloir supprimer cette sauvegarde ?')) return;
    try {
      await deleteSchoolBackup(code!, backupId);
      await fetchBackups();
    } catch (err) {
      console.error('Error deleting backup:', err);
    }
  };

  const getStatusLabel = (status: string | null) => {
    const found = options?.statuses.find((s) => s.value === status);
    return found?.label || status || '-';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (!school || !options) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">École non trouvée</p>
        <Button asChild className="mt-4">
          <Link to="/schools">Retour aux écoles</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="text-sm text-muted-foreground">
        Accueil /{' '}
        <Link to="/schools" className="hover:underline">
          Écoles
        </Link>{' '}
        / <span className="text-foreground font-medium">{school.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            {school.name}
          </h1>
          {school.vip && (
            <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700 border border-amber-300">
              VIP
            </span>
          )}
          {school.id && (
            <button
              onClick={() => toggleFavorite(school.code)}
              className="p-1 rounded hover:bg-muted transition-colors"
              title={isFavorite(school.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              <Star className={cn('h-5 w-5', isFavorite(school.id) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300')} />
            </button>
          )}
          {/* Environment badges */}
          <div className="flex items-center gap-1.5 ml-2">
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <Server className="h-3 w-3 mr-1" />
              V1 — {getStatusLabel(school.status)}
            </Badge>
            {v2Info?.deployed && (
              <Badge
                variant="outline"
                className={cn(
                  'border',
                  v2Info.tenant?.status === 'ACTIVE'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : v2Info.tenant?.status === 'SUSPENDED'
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                )}
              >
                <Server className="h-3 w-3 mr-1" />
                V2 —{' '}
                {v2Info.tenant?.status === 'ACTIVE'
                  ? 'Actif'
                  : v2Info.tenant?.status === 'SUSPENDED'
                    ? 'Suspendu'
                    : v2Info.tenant?.status === 'INACTIVE'
                      ? 'Inactif'
                      : v2Info.tenant?.status || 'Inconnu'}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{error}</div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6 mb-10">
        <StatCard title="Élèves" value={metrics?.studentCount || 0} icon={GraduationCap} color="teal" />
        <StatCard title="Parents" value={metrics?.parentCount || 0} icon={Users} color="cyan" />
        <StatCard title="Enseignants" value={metrics?.teacherCount || 0} icon={Briefcase} color="cyan" />
        <StatCard title="Classes" value={metrics?.classCount || 0} icon={Star} color="blue" />
        <StatCard title="Connexions" value={metrics?.connexionCount || 0} icon={LogIn} color="pink" />
        <StatCard title="Mails envoyes" value={metrics?.mailCount || 0} icon={Mail} color="pink" />
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" asChild>
          <Link to="/schools">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour a la liste
          </Link>
        </Button>
        <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50">
          <Trash2 className="mr-2 h-4 w-4" />
          Supprimer
        </Button>
        <Button variant="default" className="bg-blue-500 hover:bg-blue-600" asChild>
          <a href={`https://${school.code}.ecole-futee.com`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-2 h-4 w-4" />
            Connexion à l'école
          </a>
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-muted/50 p-1 rounded-lg w-fit gap-1">
          <TabsTrigger
            value="informations"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Info className="h-4 w-4 mr-1" />
            Informations
          </TabsTrigger>
          <TabsTrigger
            value="branding"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Image className="h-4 w-4 mr-1" />
            Branding
          </TabsTrigger>
          <TabsTrigger
            value="facturation"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Receipt className="h-4 w-4 mr-1" />
            Facturation
          </TabsTrigger>
          <TabsTrigger
            value="statistiques"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <BarChart3 className="h-4 w-4 mr-1" />
            Statistiques
          </TabsTrigger>
          <TabsTrigger
            value="backups"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <HardDrive className="h-4 w-4 mr-1" />
            Sauvegardes
          </TabsTrigger>
          <TabsTrigger
            value="v1"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Server className="h-4 w-4 mr-1" />
            Environnement V1
          </TabsTrigger>
          <TabsTrigger
            value="v2"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Server className="h-4 w-4 mr-1" />
            Environnement V2
          </TabsTrigger>
        </TabsList>

        {/* Informations Tab */}
        <TabsContent value="informations" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <GeneralInfoCard school={school} options={options} onSaved={refreshSchool} />
            <ContactsCard school={school} options={options} onSaved={refreshSchool} />

            {school.hasV1 && (
              <V1DeploymentCard school={school} options={options} onSaved={refreshSchool} />
            )}

            {(school.hasV2 || school.v2Subscription) && (
              <V2SubscriptionCard school={school} options={options} onSaved={refreshSchool} />
            )}
          </div>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <BrandingImagesCard school={school} onSaved={refreshSchool} />
            <WhiteLabelCard school={school} options={options} onSaved={refreshSchool} onRefreshOptions={refreshOptions} />
          </div>
        </TabsContent>

        {/* Facturation Tab */}
        <TabsContent value="facturation" className="mt-6">
          <Card>
            <CardHeader icon={<Receipt />} iconColor="orange">
              <CardTitle>Facturation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Construction className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">En cours de construction</p>
                <p className="text-sm mt-1">Cette fonctionnalite sera disponible prochainement.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Statistiques Tab */}
        <TabsContent value="statistiques" className="mt-6 space-y-6">
          {metricsHistoryLoading ? (
            <Card>
              <CardContent className="py-8">
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Chargement de l'historique...
                </div>
              </CardContent>
            </Card>
          ) : metricsHistory.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">Aucun historique disponible.</p>
              </CardContent>
            </Card>
          ) : (
            metricsHistory.map((sy) => (
              <Card key={sy.schoolYear}>
                <CardHeader icon={<BarChart3 />} iconColor="blue">
                  <CardTitle>Année fiscale : {sy.schoolYear}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right">Date</TableHead>
                          <TableHead className="text-center">Classes</TableHead>
                          <TableHead className="text-center">Enseignants</TableHead>
                          <TableHead className="text-center">Élèves</TableHead>
                          <TableHead className="text-center">Mails envoyes</TableHead>
                          <TableHead className="text-center">Connexions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sy.data.map((dp) => (
                          <TableRow key={dp.date}>
                            <TableCell className="text-right font-medium">
                              {new Date(dp.date).toLocaleDateString('fr-FR', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                              })}
                            </TableCell>
                            <TableCell className="text-center">{dp.classCount ?? '—'}</TableCell>
                            <TableCell className="text-center">{dp.teacherCount ?? '—'}</TableCell>
                            <TableCell className="text-center">{dp.studentCount ?? '—'}</TableCell>
                            <TableCell className="text-center">{dp.mailCount ?? '—'}</TableCell>
                            <TableCell className="text-center">{dp.connexionCount ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Sauvegardes Tab */}
        <TabsContent value="backups" className="mt-6">
          <Card>
            <CardHeader
              icon={<Save />}
              iconColor="teal"
              actions={
                <Button
                  size="sm"
                  onClick={handleCreateBackup}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Creer une sauvegarde
                </Button>
              }
            >
              <CardTitle>Sauvegardes</CardTitle>
            </CardHeader>
            <CardContent>
              {backupsLoading ? (
                <Skeleton className="h-32" />
              ) : backups.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Aucun resultat</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead></TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Fichier</TableHead>
                      <TableHead>Poids</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backups.map((backup) => (
                      <TableRow key={backup.id}>
                        <TableCell>
                          {backup.type === 'auto' ? (
                            <Clock className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Save className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(backup.createdAt)}</TableCell>
                        <TableCell className="font-mono text-sm">{backup.fileName}</TableCell>
                        <TableCell>{backup.sizeFormatted}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              asChild
                              className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                            >
                              <a href={getBackupDownloadUrl(code!, backup.id)} download>
                                <Download className="h-4 w-4" />
                              </a>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => handleDeleteBackup(backup.id)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* V1 Environment Tab */}
        <TabsContent value="v1" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* V1 Info Card (read-only) */}
            <Card>
              <CardHeader icon={<Database />} iconColor="blue">
                <CardTitle>Informations V1</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Version</p>
                    <Badge variant="outline">V1</Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Statut</p>
                    <Badge variant={getStatusBadgeVariant(school.status)}>
                      {getStatusLabel(school.status)}
                    </Badge>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Maintenance</p>
                    <Badge variant={school.maintenance ? 'destructive' : 'success'}>
                      {school.maintenance ? 'Oui' : 'Non'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Compte impayé</p>
                    <Badge variant={school.overdueAccount ? 'destructive' : 'success'}>
                      {school.overdueAccount ? 'Oui' : 'Non'}
                    </Badge>
                  </div>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Actions</p>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={`https://${school.code}.ecole-futee.com`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Accéder à l'école
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* V1 Database Card (editable by admins) */}
            <V1DatabaseCard school={school} onSaved={refreshSchool} />
          </div>
        </TabsContent>

        {/* V2 Environment Tab */}
        <TabsContent value="v2" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* V2 Info Card */}
            <Card>
              <CardHeader
                icon={<Database />}
                iconColor="green"
                actions={
                  !v2Info?.deployed &&
                  !v2Loading && (
                    <Button size="sm" onClick={() => setV2DeployDialogOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Deployer en V2
                    </Button>
                  )
                }
              >
                <CardTitle>Informations V2</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {v2Loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Chargement...</span>
                  </div>
                ) : v2Info?.deployed ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Version</p>
                        <Badge variant="outline">V2</Badge>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Reseau</p>
                        <p className="text-sm">
                          {options.networks.find((n) => n.id === school.networkId)?.name ||
                            'Ecole Futee (defaut)'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Tenant ID</p>
                        <p className="text-xs font-mono text-muted-foreground truncate">
                          {v2Info.tenantId}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Statut</p>
                        <Badge
                          variant={
                            v2Info.tenant?.status === 'ACTIVE'
                              ? 'success'
                              : v2Info.tenant?.status === 'SUSPENDED'
                                ? 'destructive'
                                : v2Info.tenant?.status === 'INACTIVE'
                                  ? 'warning'
                                  : 'default'
                          }
                        >
                          {v2Info.tenant?.status === 'ACTIVE'
                            ? 'Actif'
                            : v2Info.tenant?.status === 'SUSPENDED'
                              ? 'Suspendu'
                              : v2Info.tenant?.status === 'INACTIVE'
                                ? 'Inactif'
                                : v2Info.tenant?.status || 'Inconnu'}
                        </Badge>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Actions</p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={openMigrateDialog}>
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Migrer vers un autre reseau
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className={
                            v2Info.tenant?.status === 'SUSPENDED'
                              ? 'text-green-600'
                              : 'text-yellow-600'
                          }
                          onClick={handleV2Suspend}
                          disabled={v2Suspending}
                        >
                          {v2Suspending ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : v2Info.tenant?.status === 'SUSPENDED' ? (
                            'Reactiver'
                          ) : (
                            'Suspendre'
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={handleV2Reset}
                          disabled={v2Resetting}
                        >
                          {v2Resetting ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 mr-1" />
                          )}
                          Reinitialiser
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Cette école n'est pas déployée en V2</p>
                    <p className="text-sm mt-2">
                      Cliquez sur "Deployer en V2" pour demarrer le processus
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* V2 Status Card */}
            <Card>
              <CardHeader icon={<Server />} iconColor="teal">
                <CardTitle>Progression du déploiement</CardTitle>
              </CardHeader>
              <CardContent>
                {v2Loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Chargement...</span>
                  </div>
                ) : v2Info?.deployed ? (
                  <V2DeploymentStatus
                    schoolCode={school.code}
                    autoRefresh={activeTab === 'v2'}
                    refreshInterval={1000}
                  />
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    Déployez l'école en V2 pour voir la progression
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* V2 Directors Card - full width below the grid */}
          {v2Info?.deployed && (
            <div className="mt-6">
              <V2DirectorsCard schoolCode={school.code} />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* V2 Deploy Dialog */}
      <V2DeployDialog
        open={v2DeployDialogOpen}
        onOpenChange={setV2DeployDialogOpen}
        schoolCode={school.code}
        schoolName={school.name}
        onSuccess={() => {
          refreshSchool();
          fetchV2Info();
          setActiveTab('v2');
        }}
      />

      {/* V2 Migrate Dialog */}
      <Dialog open={v2MigrateDialogOpen} onOpenChange={setV2MigrateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Migrer vers un autre réseau</DialogTitle>
            <DialogDescription>
              Sélectionnez le réseau V2 vers lequel migrer l'école{' '}
              <strong>{school.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="targetGroup">Réseau cible</Label>
              <select
                id="targetGroup"
                value={v2SelectedGroupId}
                onChange={(e) => setV2SelectedGroupId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Sélectionnez un réseau...</option>
                {v2TenantGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.tenantCount} écoles)
                  </option>
                ))}
              </select>
            </div>
            {v2SelectedGroupId && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  Attention : La migration va transférer l'école vers le réseau sélectionné. Cette
                  opération peut prendre quelques instants.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setV2MigrateDialogOpen(false)}
              disabled={v2MigrateLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleV2Migrate}
              disabled={!v2SelectedGroupId || v2MigrateLoading}
            >
              {v2MigrateLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Migration...
                </>
              ) : (
                'Migrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
